// Statistics for comparing two agents' issue rates: k flagged out of n evaluated.

export type RateSample = { k: number; n: number };

export const Z_95 = 1.959964;

// Whiskers drawn at this multiple of the standard error overlap between two bars
// roughly when their rates differ at the 5% level, provided the samples are of
// similar size (Goldstein and Healy, 1995). It is the 83.4% interval.
export const Z_COMPARISON_AT_REST = Z_95 / Math.SQRT2;

// Below this many evaluated chats or messages a bar is drawn de-emphasised
export const SMALL_SAMPLE = 30;

// Agresti and Caffo: add one success and one failure before taking the standard
// error, so a rate of exactly 0 or 1 still gets a non-zero whisker and the normal
// approximation holds down to small samples
const adjustedRate = ({ k, n }: RateSample): number => (k + 1) / (n + 2);

export const standardError = (sample: RateSample): number => {
  const rate = adjustedRate(sample);
  return Math.sqrt((rate * (1 - rate)) / (sample.n + 2));
};

// The 95% interval of one rate, around the adjusted rate and inside 0 and 1
export const interval95 = (sample: RateSample): [number, number] => {
  const rate = adjustedRate(sample);
  const halfWidth = Z_95 * standardError(sample);
  return [Math.max(0, rate - halfWidth), Math.min(1, rate + halfWidth)];
};

// Half-width of a whisker on one bar such that the other bar's top falling inside
// it means the two rates do not differ at the 5% level
export const comparisonHalfWidth = (a: RateSample, b: RateSample): number =>
  Z_95 * Math.hypot(standardError(a), standardError(b));

const normalCdf = (z: number): number => {
  // Abramowitz and Stegun 7.1.26, accurate to about 1e-7
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const poly =
    t *
    (0.31938153 +
      t *
        (-0.356563782 +
          t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  const tail = (Math.exp((-z * z) / 2) / Math.sqrt(2 * Math.PI)) * poly;
  return z >= 0 ? 1 - tail : tail;
};

// Grown on demand and kept, so ranking thirty categories does not rebuild it thirty times
const logFactorialTable = [0];
const logFactorials = (upTo: number): number[] => {
  for (let i = logFactorialTable.length; i <= upTo; i++) {
    logFactorialTable.push(logFactorialTable[i - 1] + Math.log(i));
  }
  return logFactorialTable;
};

// Two-sided Fisher exact test on the 2x2 table [[kA, nA-kA], [kB, nB-kB]]: the
// probability of every table with the same margins that is at least as unlikely
const fisherExactPValue = (a: RateSample, b: RateSample): number => {
  const total = a.n + b.n;
  const flagged = a.k + b.k;
  const logFact = logFactorials(total);
  const logChoose = (n: number, k: number) =>
    logFact[n] - logFact[k] - logFact[n - k];
  const logDenominator = logChoose(total, flagged);
  const logProbability = (kA: number) =>
    logChoose(a.n, kA) + logChoose(b.n, flagged - kA) - logDenominator;
  const observed = logProbability(a.k);
  const lowest = Math.max(0, flagged - b.n);
  const highest = Math.min(a.n, flagged);
  let pValue = 0;
  for (let kA = lowest; kA <= highest; kA++) {
    const candidate = logProbability(kA);
    if (candidate <= observed + 1e-9) pValue += Math.exp(candidate);
  }
  return Math.min(1, pValue);
};

const pooledZPValue = (a: RateSample, b: RateSample): number => {
  const pooled = (a.k + b.k) / (a.n + b.n);
  const se = Math.sqrt(pooled * (1 - pooled) * (1 / a.n + 1 / b.n));
  if (se === 0) return 1;
  const z = (a.k / a.n - b.k / b.n) / se;
  return 2 * (1 - normalCdf(Math.abs(z)));
};

export type RateComparison = {
  // Rate of a minus rate of b, as a share
  difference: number;
  ciLow: number;
  ciHigh: number;
  pValue: number;
  method: 'z' | 'fisher';
};

// The normal approximation behind the z-test needs every cell of the 2x2 table to
// hold a few observations; below that the exact test is the honest one
const NORMAL_APPROXIMATION_MIN_CELL = 5;

// Null when either side has nothing evaluated, so there is no rate to compare
export const compareRates = (
  a: RateSample,
  b: RateSample
): RateComparison | null => {
  if (a.n === 0 || b.n === 0) return null;
  const difference = a.k / a.n - b.k / b.n;
  const halfWidth = comparisonHalfWidth(a, b);
  const smallestCell = Math.min(a.k, a.n - a.k, b.k, b.n - b.k);
  const useExact = smallestCell < NORMAL_APPROXIMATION_MIN_CELL;
  return {
    difference,
    ciLow: difference - halfWidth,
    ciHigh: difference + halfWidth,
    pValue: useExact ? fisherExactPValue(a, b) : pooledZPValue(a, b),
    method: useExact ? 'fisher' : 'z',
  };
};

// Whisker half-widths, as shares, chosen so that two whiskers overlapping reads as
// "these rates do not differ at the 5% level". At rest each is its own 83% interval,
// which makes that reading right when the bars have similar standard errors. Against a
// reference bar, whose own whisker collapses, every other whisker is the exact comparison
// with it. With two bars and no reference, each carries half of that comparison.
export const whiskerHalfWidths = (
  samples: RateSample[],
  referenceIndex: number | null
): number[] => {
  if (referenceIndex !== null) {
    const reference = samples[referenceIndex];
    return samples.map((sample, index) =>
      index === referenceIndex ? 0 : comparisonHalfWidth(sample, reference)
    );
  }
  if (samples.length === 2) {
    const shared = comparisonHalfWidth(samples[0], samples[1]) / 2;
    return [shared, shared];
  }
  return samples.map((sample) => Z_COMPARISON_AT_REST * standardError(sample));
};
